import * as React from "react";
import { Pencil, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../primitives/Button";
import { Input } from "../primitives/Input";
import { cn } from "../lib/cn";

export interface MagicEditButtonProps {
  /** Called with the trimmed instruction when the user submits the edit. */
  onEdit: (prompt: string) => void;
  /** Name of the thing being edited, woven into the labels (e.g. `"title"`). */
  sectionName?: string;
  /** Disables submit (e.g. while a previous edit is still applying). */
  disabled?: boolean;
  /** Which side the popover opens on. */
  side?: "top" | "right" | "bottom" | "left";
  /** Extra classes for the trigger button. */
  className?: string;
}

/**
 * MagicEditButton — an inline "edit this with AI" affordance: a pencil trigger
 * that opens a small popover with a one-line instruction field and a send
 * button (ported library-clean from the creation wizard's MagicEditButton).
 *
 * `onEdit` receives the trimmed instruction; the popover clears and closes on
 * submit. Composes the `Popover` + `Button` + `Input` primitives, so Radix owns
 * focus management (the field is focused on open) and the trigger's
 * `aria-expanded` / `aria-haspopup`. Brand tokens only; the trigger reads in
 * `primary` while open.
 *
 * Both controls inherit the Onyx press language from `Button` (this is a pure
 * composition — no raw faces here), and both are icon-only sizes, which render
 * FLAT: no travel block (behind a 32px circle the offset block reads as a
 * detached crescent, PlatformQA#2829), a springy 1px press-DOWN on `:active`.
 * The ghost pencil trigger is quiet (no keyline ring); the primary submit
 * carries the filled face + rim. So this affordance presses consistently with
 * the rest of the lib with zero local press/notch code.
 */
export function MagicEditButton({
  onEdit,
  sectionName,
  disabled,
  side,
  className,
}: MagicEditButtonProps) {
  const [open, setOpen] = React.useState(false);
  const [prompt, setPrompt] = React.useState("");
  const target = sectionName || "this section";

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = prompt.trim();
    if (!trimmed || disabled) return;
    onEdit(trimmed);
    setPrompt("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Edit ${target} with AI`}
          title={`Edit ${target} with AI`}
          className={cn(open && "bg-primary text-primary-foreground hover:bg-primary/90", className)}
        >
          {/* The glyph stays a pencil in BOTH states; the open state is the
              pressed fill above + Radix's aria-expanded. It used to morph to an
              ✕, but pencil→✕ names two different actions (edit vs dismiss), and
              a bare ✕ beside a host's primary CTA (Unified renders this next to
              "Approve and continue") reads as reject-the-draft (PlatformQA#2829).
              Dismissal stays on the popover's own routes: Esc / click-outside. */}
          <Pencil className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" side={side} sideOffset={8} className="w-80 p-2">
        <form onSubmit={submit} className="flex items-center gap-2">
          <Input
            type="text"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder={`How should I change ${sectionName || "this"}?`}
            aria-label={`Edit instruction for ${target}`}
            /* variant="ghost" rather than hand-rolling one onto the default
               variant. The hand-rolled form set border-0/bg-transparent/ring-0
               but could not reach the base's ring OFFSET, so a focused field
               painted a 2px --background box (live in Unified via
               CatalogFocusHost). Rounding differs between the variants and is
               immaterial here: nothing draws a border or a background to round. */
            variant="ghost"
            className="h-8 flex-1 px-1 shadow-none"
          />
          <Button type="submit" size="icon-xs" disabled={!prompt.trim() || disabled} aria-label="Apply edit">
            <Send className="size-3.5" />
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
